import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Box, Button, Typography } from '@mui/material';
import { t } from 'i18next';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    generatePath,
    useLocation,
    useNavigate,
    useParams,
} from 'react-router-dom';
import { InvoicesAnalytics } from 'shared/analytics/invoices/invoices';
import { INVOICE_STATUS } from 'shared/constants/invoices/statuses';
import Invoice from 'shared/db/services/Invoice';
import OrganisationAccountings from 'shared/db/services/OrganisationAccountings';
import { InvoiceModel } from 'shared/types/Invoice';
import { OrganisationAccountingsModel } from 'shared/types/OrganisationAccountings';

import { BackLinkButton, ErrorState, RemoveEntityAlert } from '@/components';
import AddEditInvoiceModal from '@/components/AddEditInvoiceModal';
import TooltipSyncButton from '@/components/TooltipButton';
import { useDBSyncContext } from '@/context/DBSyncContext';
import { useImagesContext } from '@/context/ImagesContext';
import { useInvoicingSystemContext } from '@/context/InvoicingSystemContext';
import { useDatabase } from '@/hooks';
import { ROUTE } from '@/router/routes';
import { databaseValidation } from '@/services/databaseValidation';
import { FirebaseAnalytics } from '@/services/firebase/analytics';
import Logger from '@/services/logger';
import { dbServiceLogger } from '@/services/logger/logger';
import { Snackbar } from '@/services/toastNotifications';

import InvoicingNotAvailableSection from '../InvoicesPage/components/InvoicingNotAvailableSection';

import InvoiceDetails from './components';

function InvoicePage() {
    const { ImagesService } = useImagesContext();
    const { isSyncInProgress } = useDBSyncContext();
    const { isInvoicingAvailable, isInvoiceSystemEnabled } =
        useInvoicingSystemContext();
    const navigate = useNavigate();
    const location = useLocation();
    const archivedContext = location.state as {
        fromArchived?: boolean;
        provider?: string;
        providerName?: string;
        totalCount?: number;
    } | null;

    const db = useDatabase();
    const database = useMemo(() => db.getDatabase(), [db]);

    const { id } = useParams();

    const [invoice, setInvoice] = useState<InvoiceModel | null>(null);

    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isRemoveAlertOpen, setIsRemoveAlertOpen] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isError, setIsError] = useState(false);

    const openEditModal = useCallback(() => setIsEditModalOpen(true), []);
    const closeEditModal = useCallback(() => setIsEditModalOpen(false), []);

    const [accountings, setAccountings] =
        useState<OrganisationAccountingsModel | null>(null);

    const handleInvoiceRemoved = useCallback(
        () => navigate(ROUTE.invoices),
        [navigate],
    );

    const invoiceService = useMemo(
        () =>
            new Invoice({
                database,
                imageService: ImagesService,
                logger: dbServiceLogger,
                validation: databaseValidation,
            }),
        [ImagesService, database],
    );

    const fetchInvoice = useCallback(async () => {
        setIsError(false);

        try {
            if (!id) {
                throw new Error();
            }

            const invoice = await invoiceService.getByID(id);
            setInvoice(invoice);
        } catch {
            setIsError(true);
        } finally {
            setIsLoading(false);
        }
    }, [id, invoiceService]);

    useEffect(() => {
        fetchInvoice();
    }, [fetchInvoice]);

    const fetchAccountings = useCallback(async () => {
        const accountingsService = new OrganisationAccountings({
            database,
            logDBAction: Logger.logRecordActivity,
            validation: databaseValidation,
        });

        const [accountingsData] = await accountingsService.get().fetch();

        setAccountings(accountingsData);
    }, [database]);

    useEffect(() => {
        fetchAccountings();
    }, [fetchAccountings]);

    const isDeleteButtonEnabled = useMemo(() => {
        return (
            (!accountings?.accountingProvider &&
                invoice?.status === INVOICE_STATUS.paid) ||
            (!accountings?.accountingProvider &&
                invoice?.status === INVOICE_STATUS.authorised) ||
            (!accountings?.accountingProvider &&
                invoice?.status === INVOICE_STATUS.approved) ||
            invoice?.status === INVOICE_STATUS.draft
        );
    }, [accountings, invoice?.status]);

    const openDeleteAlert = useCallback(() => setIsRemoveAlertOpen(true), []);
    const closeDeleteAlert = useCallback(() => setIsRemoveAlertOpen(false), []);

    const removeInvoice = useCallback(async () => {
        if (invoice) {
            try {
                await invoiceService.deleteByID(invoice.id);

                InvoicesAnalytics.logUserDeletedInvoice(
                    FirebaseAnalytics.logEvent,
                );

                Snackbar.showToastNotification({
                    message: t('SingleInvoice:alert:invoice_removed'),
                });

                navigate(ROUTE.invoices, { replace: true });
            } catch {
                Snackbar.showToastNotification({
                    message: t('App:Messages:something_went_wrong'),
                    options: {
                        variant: 'error',
                    },
                });
            }
        }
    }, [invoice, invoiceService, navigate]);

    if (!isInvoicingAvailable) {
        return <InvoicingNotAvailableSection />;
    }

    return (
        <Box sx={{ pb: 10, minWidth: 400 }}>
            {isEditModalOpen && invoice ? (
                <AddEditInvoiceModal
                    close={closeEditModal}
                    onRemove={handleInvoiceRemoved}
                    invoice={invoice}
                    isEditMode
                />
            ) : null}
            {invoice?.providerArchivedAt && archivedContext?.fromArchived ? (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 2,
                        py: 1.5,
                        mb: 2,
                        bgcolor: '#D32F2F',
                        borderRadius: 1,
                        color: 'white',
                    }}
                >
                    <ErrorOutlineIcon sx={{ color: 'white', fontSize: 20 }} />
                    <Typography fontSize={14} fontWeight={600} color="white">
                        {t('Invoicing:disconnected_from_provider', {
                            defaultValue: 'Disconnected from {{provider}}',
                            provider:
                                archivedContext.providerName ??
                                invoice.provider,
                        })}
                    </Typography>
                    {invoice.providerArchivedAt && (
                        <Typography fontSize={14} color="white" sx={{ ml: 1 }}>
                            {t('Invoicing:last_synced_on', {
                                defaultValue: 'Last synced on {{date}}',
                                date: new Date(
                                    invoice.providerArchivedAt,
                                ).toLocaleDateString(),
                            })}
                        </Typography>
                    )}
                </Box>
            ) : null}
            <Box
                sx={{
                    mb: 3.25,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <BackLinkButton
                    text={
                        archivedContext?.fromArchived
                            ? `${
                                  archivedContext.providerName ??
                                  t('Invoicing:archived_invoices', {
                                      defaultValue: 'Archived Invoices',
                                  })
                              } (${archivedContext.totalCount ?? ''})`
                            : t('SingleInvoice:button:back')
                    }
                    to={
                        archivedContext?.fromArchived &&
                        archivedContext.provider
                            ? (generatePath(ROUTE.archivedInvoicesList, {
                                  provider: archivedContext.provider,
                              }) as ROUTE)
                            : ROUTE.invoices
                    }
                    testID="InvoicePage-BackButton"
                />
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                    {!archivedContext?.fromArchived &&
                    (invoice?.status === INVOICE_STATUS.draft ||
                        (isInvoiceSystemEnabled &&
                            invoice?.provider === 'internal' &&
                            invoice?.status === INVOICE_STATUS.approved)) ? (
                        <Button
                            data-test-id="InvoicePage-EditButton"
                            sx={{ display: 'flex', gap: 1 }}
                            color="inherit"
                            size="small"
                            disabled={isLoading || isError}
                            onClick={openEditModal}
                        >
                            <EditIcon />
                            <Typography fontSize="small" fontWeight={700}>
                                {t('SingleInvoice:button:edit')}
                            </Typography>
                        </Button>
                    ) : undefined}

                    {isDeleteButtonEnabled && !archivedContext?.fromArchived ? (
                        <TooltipSyncButton
                            tooltip={t('AddEditModalHeader:button_tooltip', {
                                defaultValue:
                                    'Please wait for the synchronization to complete',
                            })}
                            disabled={isLoading || isError || isSyncInProgress}
                            showTooltip={isSyncInProgress}
                            testID="InvoicePage-DeleteButton"
                            sx={{ display: 'flex', gap: 1 }}
                            color="inherit"
                            onClick={openDeleteAlert}
                        >
                            <DeleteIcon />
                            <Typography fontSize="small" fontWeight={700}>
                                {t('SingleInvoice:button:delete')}
                            </Typography>
                        </TooltipSyncButton>
                    ) : undefined}
                </Box>
            </Box>
            {isError ? (
                <ErrorState
                    onRefreshClick={fetchInvoice}
                    testID="InvoicePage-ErrorState"
                />
            ) : (
                <InvoiceDetails
                    invoice={invoice}
                    refetchInvoice={fetchInvoice}
                    isLoading={isLoading}
                />
            )}
            <RemoveEntityAlert
                dialogText={t('SingleInvoice:alert:invoice_remove', {
                    ref: invoice?.reference,
                })}
                isOpen={isRemoveAlertOpen}
                onClose={closeDeleteAlert}
                onRemove={removeInvoice}
                testID="RemoveInvoiceAlert"
            />
        </Box>
    );
}

export default InvoicePage;
